/* eslint-disable prefer-rest-params */
import { FirebaseError } from 'firebase/app';
import {
  collection,
  type CollectionReference,
  deleteDoc,
  deleteField,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type FieldPath,
  type FieldValue,
  type FirestoreErrorCode,
  getDoc,
  getDocFromCache,
  getDocFromServer,
  increment,
  onSnapshot,
  type PartialWithFieldValue,
  serverTimestamp,
  setDoc,
  type SetOptions,
  type UpdateData,
  updateDoc,
  type WithFieldValue,
  type WriteBatch,
} from 'firebase/firestore';
import { Observable } from 'rxjs';
import { getAuth } from '../firebase/firestore';
import { pathToIds } from '../helpers/pathToIds';
import { pruneObject } from '../helpers/pruneObject';
import { translate } from '../helpers/translate';
import type { Constructable, DocumentSourced, Identified, PartialMaybe, PartialRecursive, Timestamped } from './_';
import type { AbstractCollection } from './Collection';

export abstract class AbstractDocument<T> {
  readonly collections: Record<string, AbstractCollection<any, object>> = {};
  readonly reference: DocumentReference<T>;

  readonly translatable?: (data: any) => unknown;

  #ids: Record<string, string> | null = null;
  protected batch?: WriteBatch;

  constructor(reference: DocumentReference<T>, batch?: WriteBatch) {
    if (batch != null) {
      this.batch = batch;
    }

    this.reference = reference;
  }

  /**
   * Basename of the document.
   */
  get id() {
    return this.reference.id;
  }

  /**
   * Returns an object mapping each collection name in the path to its corresponding document ID.
   */
  get ids() {
    if (this.#ids == null) {
      this.#ids = pathToIds(this.path);
    }

    return this.#ids;
  }

  /**
   * Native Firestore instance.
   */
  get db() {
    return this.reference.firestore;
  }

  /**
   * Absolute path to this (sub-)document.
   */
  get path() {
    return this.reference.path;
  }

  /**
   * @param id Basename of the sub-collection.
   */
  collection<S = DocumentData>(id: string) {
    return collection(this.reference, id) as CollectionReference<S>;
  }

  /**
   * Creates a document with the supplied `data` and returns itself.
   *
   * @param data The data to write.
   */
  async create(data: WithFieldValue<PartialMaybe<T, Identified & Timestamped>>) {
    try {
      await this.set(data, false);
    } catch (error) {
      if (error instanceof FirebaseError && error.code === 'invalid-argument') {
        await this.set(data, error.message.includes('cannot be used with set() unless'));
      }
    }

    return this;
  }

  /**
   * Atomically decrements the value at the specified path by one.
   *
   * @param path Dot-separated path to decrement.
   */
  decrement(path: keyof UpdateData<T>) {
    return this.update({ [path]: increment(-1) } as UpdateData<T>);
  }

  /**
   * Deletes the document and returns how many documents were deleted.
   *
   * @param deep Deletes all sub-collections as well.
   */
  async delete(deep = true) {
    let result = 0;

    if (deep === true) {
      const promises: Promise<number>[] = [];

      for (const collection in this.collections) {
        promises.push(this.collections[collection].delete(deep));
      }

      await Promise.all(promises).then((counts) => {
        for (const count of counts) {
          result += count;
        }
      });
    }

    if (this.batch == null) {
      try {
        await deleteDoc(this.reference);
      } catch (error) {
        if (error instanceof Error) {
          if (error.message.includes(this.path) !== true) {
            error.message = [`Failed to delete() on '${this.path}'.`, error.message].join('\n');
          }
        }

        throw error;
      }
    } else {
      this.batch.delete(this.reference);
    }

    return ++result;
  }

  get(data: false): Promise<DocumentSnapshot<T>>;
  get(data?: true): Promise<T>;

  /**
   * Returns a promise containing either the typed document or a snapshot.
   *
   * @param data If `false`, returns a `DocumentSnapshot` instead.
   */
  get(data?: boolean) {
    return getDoc(this.reference).then((document) => (data === false ? document : document.data()));
  }

  /**
   * Returns a promise with the typed document.
   * The most adequate translation overwrites the original content.
   *
   * @param locale The target language (BCP 47).
   */
  getTranslated(locale?: string): Promise<T> {
    // @ts-ignore --- Ignoring this error until we type the `translate()` function properly.
    return this.get(true).then((data) => translate(data, locale));
  }

  /**
   * Returns an object with all translatable properties.
   * This needs to be implemented in each supported model.
   */
  async getTranslatableProps(snapshot?: T): Promise<PartialRecursive<T>> {
    if (this.translatable == null) {
      throw new Error(`Method not implemented. Translatable properties need to be defined in ${this.reference.parent.path} model.`);
    }

    return pruneObject(this.translatable(snapshot != null ? snapshot : await this.get(true)));
  }

  /**
   * Atomically increments the value at the specified path by one.
   *
   * @param path Dot-separated path to increment.
   */
  increment(path: keyof UpdateData<T>) {
    return this.update({ [path]: increment(+1) } as UpdateData<T>);
  }

  observe(data: false, source?: false): Observable<DocumentSnapshot<T>>;
  observe(data: false, source?: true): Observable<DocumentSourced<DocumentSnapshot<T>>>;
  observe(data?: true, source?: false): Observable<T>;
  observe(data?: true, source?: true): Observable<DocumentSourced<T>>;

  /**
   * Returns an observable containing either the typed document or a snapshot.
   *
   * @param data If `false`, returns a `DocumentSnapshot` instead.
   * @param source If `true`, returns `local` if there are pending writes, or `server` otherwise.
   */
  observe(data?: boolean, source = false) {
    return new Observable((subscriber) => {
      const callback = (snapshot: DocumentSnapshot<T>) => {
        const result = data === false ? snapshot : snapshot.data();

        if (source !== true) {
          return subscriber.next(result);
        }

        return subscriber.next({
          result,
          source: snapshot.metadata.hasPendingWrites ? 'local' : 'server',
          fromCache: snapshot.metadata.fromCache,
        });
      };

      return onSnapshot(this.reference, callback, (error: Error) => {
        if (error instanceof FirebaseError) {
          const code = error.code as FirestoreErrorCode;

          if (code === 'aborted' && error.message === 'Firestore shutting down') {
            return subscriber.complete();
          }
        }

        return subscriber.error(error);
      });
    });
  }

  observeTranslated(locale?: string, source?: false): Observable<T>;
  observeTranslated(locale?: string, source?: true): Observable<DocumentSourced<T>>;

  /**
   * Returns an observable containing the typed document.
   * The most adequate translation overwrites the original content.
   *
   * @param locale The target language (BCP 47).
   * @param source If `true`, returns `local` if there are pending writes, or `server` otherwise.
   */
  observeTranslated(locale?: string, source = false) {
    return new Observable((subscriber) => {
      const callback = (snapshot: DocumentSnapshot<T>) => {
        // @ts-ignore --- Ignoring this error until we type the `translate()` function properly.
        const result = translate(snapshot.data(), locale);

        if (source !== true) {
          return subscriber.next(result);
        }

        return subscriber.next({
          result,
          source: snapshot.metadata.hasPendingWrites ? 'local' : 'server',
        });
      };

      return onSnapshot(this.reference, callback, (error: Error) => {
        if (error instanceof FirebaseError) {
          const code = error.code as FirestoreErrorCode;

          if (code === 'aborted' && error.message === 'Firestore shutting down') {
            return subscriber.complete();
          }
        }

        return subscriber.error(error);
      });
    });
  }

  /**
   * Removes a one or multiple dot-separated properties from the document.
   * WARNING: These arguments can be misleading, be sure to know what you're doing!
   *
   * @param path Dot-separated paths to remove.
   */
  remove(...paths: (keyof UpdateData<T>)[]) {
    const data: Record<PropertyKey, FieldValue> = {};

    for (const path of paths) {
      data[path] = deleteField();
    }

    return this.update(data as UpdateData<T>);
  }

  /**
   * Soft deletes the document by setting `deletedAt` timestamp.
   * WARNING: Only use iff the model supports soft deletes.
   */
  async trash() {
    // @ts-expect-error
    await this.update({ deletedAt: serverTimestamp() });
  }

  /**
   * Writes the supplied `data` to the document and returns the document ID.
   *
   * @param data The data to write.
   * @param merge Merges the entire object if `true`, or replaces the specified paths.
   */
  async set(data: PartialWithFieldValue<PartialMaybe<T, Identified & Timestamped>>, merge: boolean | (string | FieldPath)[] = true) {
    const options: SetOptions = {
      [typeof merge === 'boolean' ? 'merge' : 'mergeFields']: merge,
    };

    if (this.batch == null) {
      try {
        await setDoc(this.reference, data as PartialWithFieldValue<T>, options);
      } catch (error) {
        if (error instanceof Error) {
          if (error.message.includes(this.path) !== true) {
            error.message = [`Failed to set() on '${this.path}'.`, error.message].join('\n');
          }
        }

        throw error;
      }
    } else {
      this.batch.set(this.reference, data as PartialWithFieldValue<T>, options);
    }

    return this.reference.id;
  }

  /**
   * Updates properties specified by keys in `data` with their values and returns the document ID.
   * WARNING: These arguments can be misleading, be sure to know what you're doing!
   *
   * @param data Key-value dot-separated path and corresponding values to write.
   */
  async update(data: PartialWithFieldValue<T> | UpdateData<T>) {
    if (this.batch == null) {
      try {
        await updateDoc(this.reference, data as UpdateData<DocumentData>);
      } catch (error) {
        if (error instanceof Error) {
          if (error.message.includes(this.path) !== true) {
            error.message = [`Failed to update() on '${this.path}'.`, error.message].join('\n');
          }
        }

        throw error;
      }
    } else {
      this.batch.update(this.reference, data as UpdateData<DocumentData>);
    }

    return this.reference.id;
  }
}

/**
 * Reads the document from the cache.
 * If no local cache exists for the document, reads the document from the server.
 */
export function Cached<T extends AbstractDocument<object>>(target: Constructable<T>) {
  const get = target.prototype.get;

  target.prototype.get = async function (data?: boolean) {
    let document: DocumentSnapshot;

    try {
      document = await getDocFromCache(this.reference);
    } catch {
      try {
        document = await getDocFromServer(this.reference);
      } catch {
        return get.apply(this, arguments);
      }
    }

    return data === false ? document : document.data();
  };
}

/**
 * Automatically populates the `id` field in the document, if not present.
 */
export function Identifiable<T extends AbstractDocument<object>>(target: Constructable<T>) {
  const create = target.prototype.create;

  target.prototype.create = function (data: any) {
    if (data.id === undefined) {
      data.id = this.id;
    }

    return create.apply(this, arguments);
  };

  return target;
}

/**
 * Automatically populates the `shape` field in the document, if not present.
 */
export function Shapeable<T extends AbstractDocument<object>>(target: Constructable<T>) {
  const create = target.prototype.create;

  target.prototype.create = function (data: any) {
    if (data.shape === undefined) {
      data.shape = '';
    }

    return create.apply(this, arguments);
  };
}

/**
 * Automatically populates the `createdAt` & `updatedAt` fields in the document, if not present.
 */
export function Timestampable<T extends AbstractDocument<object>>(target: Constructable<T>) {
  const create = target.prototype.create;

  target.prototype.create = function (data: any) {
    if (data.createdAt === undefined) {
      data.createdAt = serverTimestamp();
    }

    return create.apply(this, arguments);
  };

  const set = target.prototype.set;

  target.prototype.set = function (data: any, merge: boolean | (string | FieldPath)[] = true) {
    if (data.updatedAt === undefined) {
      data.updatedAt = serverTimestamp();
    }

    if (typeof merge !== 'boolean') {
      if (data.createdAt !== undefined && merge.includes('createdAt') !== true) {
        merge.push('createdAt');
      }

      if (data.updatedAt !== undefined && merge.includes('updatedAt') !== true) {
        merge.push('updatedAt');
      }
    }

    return set.apply(this, arguments);
  };

  const update = target.prototype.update;

  target.prototype.update = function (data: any) {
    if (data.updatedAt === undefined) {
      data.updatedAt = serverTimestamp();
    }

    return update.apply(this, arguments);
  };

  return target;
}

export function Trackable<T extends AbstractDocument<object>>(target: Constructable<T>) {
  const create = target.prototype.create;

  target.prototype.create = function (data: any) {
    data.authoredBy = getAuth().currentUser?.uid ?? null;

    return create.apply(this, arguments);
  };

  const set = target.prototype.set;

  target.prototype.set = function (data: any, merge: boolean | (string | FieldPath)[] = true) {
    data.authoredBy = getAuth().currentUser?.uid ?? null;

    if (typeof merge !== 'boolean') {
      if (data.authoredBy !== undefined && merge.includes('authoredBy') !== true) {
        merge.push('authoredBy');
      }
    }

    return set.apply(this, arguments);
  };

  const update = target.prototype.update;

  target.prototype.update = function (data: any) {
    data.authoredBy = getAuth().currentUser?.uid ?? null;

    return update.apply(this, arguments);
  };

  return target;
}

/**
 * Cleans the `authoredBy` field from the document, which is now deprecated.
 *
 * This should progressively clean the database, avoiding having to issue hundreds of millions of
 * writes in bulk.
 */
export function Untrackable<T extends AbstractDocument<object>>(target: Constructable<T>) {
  const set = target.prototype.set;

  target.prototype.set = function (data: any, merge: boolean | (string | FieldPath)[] = true) {
    if (typeof merge !== 'boolean') {
      data.authoredBy = deleteField();

      if (data.authoredBy !== undefined && merge.includes('authoredBy') !== true) {
        merge.push('authoredBy');
      }
    } else if (merge === true) {
      data.authoredBy = deleteField();
    }

    return set.apply(this, arguments);
  };

  const update = target.prototype.update;

  target.prototype.update = function (data: any) {
    data.authoredBy = deleteField();

    return update.apply(this, arguments);
  };

  return target;
}
